import api from '../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Tooltip,
  Drawer,
  Tabs,
  Descriptions,
  Badge,
  ConfigProvider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  UserOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  DollarOutlined,
  CreditCardOutlined,
  MoneyCollectOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import config from "../../config";
import { usePermissions } from "../../permissions";
import StakeholderForm from "./StakeholderForm";
import { formatKES } from "../../utils/currency";
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import ProTable from "@ant-design/pro-table";
import enUS from "antd/lib/locale/en_US";
import AddCustomerCreditModal from "./AddCustomerCreditModal";
import { FaMoneyBill, FaMoneyBillAlt, FaMoneyBillWave } from "react-icons/fa";

const { Option } = Select;

const Stakeholders = () => {
  const history = useHistory();
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    customers: 0,
    suppliers: 0,
    veterinary: 0,
    total_receivable: 0,
    total_payable: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const { createPermission, editPermission, deletePermission } =
    usePermissions();
  const currentUser = useSelector((state) => state.account.user);

  const [creditModalVisible, setCreditModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchStakeholders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (typeFilter) params.type = typeFilter;
      const { data } = await api.get(`/api/stakeholders`, {
        params,
      });
      setStakeholders(data);
    } catch (err) {
      message.error("Failed to fetch stakeholders");
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get(`/stakeholders/stats`);
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchStakeholders();
    fetchStats();
  }, [fetchStakeholders, fetchStats]);

  const filteredStakeholders = stakeholders.filter((s) => {
    const matchesSearch = searchText
      ? s?.name?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
        s?.email?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
        s?.phone?.includes(searchText)
      : true;
    const matchesType = typeFilter ? s.type === typeFilter : true;
    return matchesSearch && matchesType;
  });

  const handleDelete = async (id, name) => {
    try {
      await api.put(`/stakeholders/${id}/archive`);
      message.success(`${name} archived`);
      fetchStakeholders();
      fetchStats();
    } catch (err) {
      message.error("Archive failed");
    }
  };

  const exportToCSV = () => {
    if (!filteredStakeholders.length) return message.warning("No data");
    const headers = [
      "Name",
      "Email",
      "Phone",
      "Type",
      "Receivable",
      "Payable",
      "KRA PIN",
      "Location",
    ];
    const rows = filteredStakeholders.map((s) => [
      s.name,
      s.email,
      s.phone,
      s.type,
      s.receivable || 0,
      s.payable || 0,
      s.kra_pin || "",
      s.location || "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, `stakeholders_${dayjs().format("YYYYMMDD")}.csv`);
    message.success("CSV exported");
  };

  const exportToPDF = () => {
    if (!filteredStakeholders.length) return message.warning("No data");
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        ["Name", "Email", "Phone", "Type", "Receivable", "Payable", "KRA PIN"],
      ],
      body: filteredStakeholders.map((s) => [
        s.name,
        s.email,
        s.phone,
        s.type,
        s.receivable || 0,
        s.payable || 0,
        s.kra_pin || "",
      ]),
    });
    doc.save(`stakeholders_${dayjs().format("YYYYMMDD")}.pdf`);
    message.success("PDF exported");
  };

  const handleView = (user) => {
    history.push(`/stakeholder/details/${user.id}`);
  };
  // Statistics
  const total = stakeholders.length;
  const customers = stakeholders.filter((s) => s.type === "customer").length;
  const suppliers = stakeholders.filter((s) => s.type === "supplier").length;
  const veterinary = stakeholders.filter((s) => s.type === "veterinary").length;
  const totalReceivable = stakeholders.reduce(
    (sum, s) => sum + (s.receivable || 0),
    0,
  );
  const totalPayable = stakeholders.reduce(
    (sum, s) => sum + (s.payable || 0),
    0,
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, record) => (
        <Link to={`/stakeholder/details/${record.id}`}>{record.name}</Link>
      ),
    },
    { title: "Email", dataIndex: "email" },
    { title: "Phone", dataIndex: "phone" },
    {
      title: "Type",
      dataIndex: "type",
      valueEnum: {
        customer: { text: "Customer", status: "Success" },
        veterinary: { text: "Veterinary", status: "error" },
        supplier: { text: "Supplier", status: "Warning" },
      },
      filters: [
        { text: "Customer", value: "customer" },
        { text: "Supplier", value: "supplier" },
        { text: "Veterinary", value: "veterinary" },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Receivable (KES)",
      dataIndex: "receivable",
      render: (v) => formatKES(v) || " ",
      align: "right",
    },
    {
      title: "Payable (KES)",
      dataIndex: "payable",
      render: (v) => formatKES(v) || " ",
      align: "right",
    },
    // { title: "KRA PIN", dataIndex: "kra_pin", render: (v) => v || "—" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<UserOutlined />}
              size="small"
              onClick={() => {
                setSelectedStakeholder(record);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
          {editPermission && (
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => {
                  setEditingStakeholder(record);
                  setModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {record.type === "customer" ? (
            <Tooltip title="Add Credit">
              <Button
              size="small"
                icon={<CreditCardOutlined />}
                onClick={() => {
                  setSelectedCustomer(record);
                  setCreditModalVisible(true);
                }}
              />
            </Tooltip>
          ):  <Tooltip title="Add Credit">
              <Button
              size="small"
                icon={<CreditCardOutlined />}
                onClick={() => {
                  setSelectedCustomer(record);
                  setCreditModalVisible(true);
                }}
                disabled
              />
            </Tooltip>}
          {deletePermission && (
            <Popconfirm
              title="Archive this stakeholder?"
              onConfirm={() => handleDelete(record.id, record.name)}
            >
              <Tooltip title="Archive">
                <Button icon={<DeleteOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={4}>
          <Card>
            <Statistic
              title="Total Stakeholders"
              value={total}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card>
            <Statistic
              title="Customers"
              value={customers}
              prefix={<UserOutlined />}
              // valueStyle={{ color: "#1677ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card>
            <Statistic
              title="Suppliers"
              value={suppliers}
              prefix={<TeamOutlined />}
              // valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card>
            <Statistic
              title="Veterinary"
              value={veterinary}
              prefix={<MedicineBoxOutlined />}
              // valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card>
            <Statistic
              title="Total Receivables"
              value={totalReceivable}
              prefix={<FaMoneyBillWave />}
              precision={2}
              // valueStyle={{ color: "#1677ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card>
            <Statistic
              title="Total Payables"
              value={totalPayable}
              prefix={<FaMoneyBillAlt />}
              precision={2}
              // valueStyle={{ color: "#f5222d" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        style={{ marginTop: 24 }}
        title="Stakeholders"
        extra={
          <Space>
            <Input.Search
              placeholder="Search by name/email"
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
            />
            <Select
              allowClear
              placeholder="Type"
              style={{ width: 120 }}
              onChange={setTypeFilter}
              options={[
                { label: "Customer", value: "customer" },
                { label: "Supplier", value: "supplier" },
                { label: "Veterinary", value: "veterinary" },
              ]}
            />
            <Tooltip title="Export CSV">
              <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
                CSV
              </Button>
            </Tooltip>
            <Tooltip title="Export PDF">
              <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
                PDF
              </Button>
            </Tooltip>
            {createPermission && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingStakeholder(null);
                  setModalVisible(true);
                }}
              >
                Add Stakeholder
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchStakeholders();
                fetchStats();
              }}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <ProTable
          defaultSize={"small"}
          columns={columns}
          dataSource={filteredStakeholders}
          rowKey="id"
          loading={loading}
          // pagination={{ pageSize: 20 }}
          scroll={{ x: "max-content" }}
          search={false}
        />
      </Card>

      {/* Add/Edit Form Modal */}
      <StakeholderForm
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingStakeholder(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setEditingStakeholder(null);
          fetchStakeholders();
          fetchStats();
        }}
        initialData={editingStakeholder}
        apiUrl={apiUrl}
      />

      {/* Details Drawer */}
      <Drawer
        title="Stakeholder Details"
        width={600}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedStakeholder && (
          <>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Name">
                {selectedStakeholder.name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedStakeholder.email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedStakeholder.phone}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag
                  color={
                    selectedStakeholder.type === "customer" ? "blue" : "orange"
                  }
                >
                  {selectedStakeholder.type}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="KRA PIN">
                {selectedStakeholder.kra_pin || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Location">
                {selectedStakeholder.location || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Receivable">
                <strong>
                  KES {selectedStakeholder.receivable?.toLocaleString()}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="Payable">
                <strong>
                  KES {selectedStakeholder.payable?.toLocaleString()}
                </strong>
              </Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 16 }}>
              <h4>Transactions</h4>
              <Table
                dataSource={selectedStakeholder.transactions || []}
                columns={[
                  {
                    title: "Date",
                    dataIndex: "created_at",
                    render: (d) => dayjs(d).format("DD/MM/YYYY"),
                  },
                  {
                    title: "Type",
                    dataIndex: "transaction_type",
                    render: (t) => (
                      <Tag color={t === "invoice" ? "blue" : "orange"}>{t}</Tag>
                    ),
                  },
                  { title: "Reference", dataIndex: "reference" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    render: (v) => `KES ${v?.toLocaleString()}`,
                  },
                  {
                    title: "Paid",
                    dataIndex: "paid_amount",
                    render: (v) => `KES ${v?.toLocaleString()}`,
                  },
                  {
                    title: "Balance",
                    render: (_, r) =>
                      `KES ${(r.amount - r.paid_amount).toLocaleString()}`,
                  },
                ]}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </div>
          </>
        )}
      </Drawer>

      <AddCustomerCreditModal
        visible={creditModalVisible}
        customerId={selectedCustomer?.id}
        customerName={selectedCustomer?.name}
        onClose={() => {
          setCreditModalVisible(false);
          setSelectedCustomer(null);
        }}
        onSuccess={() => {
          // Refresh customer list or credit balance
          fetchStakeholders();
        }}
        currentUser={currentUser}
      />
    </>
  );
};

export default Stakeholders;
